import { forwardRef, type ButtonHTMLAttributes, type HTMLAttributes, type ReactNode } from 'react';

function join(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ');
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  busy?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', busy = false, className, children, disabled, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      className={join('dd-button', `dd-button--${variant}`, className)}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...props}
    >
      {busy ? <span className="dd-spinner" aria-hidden="true" /> : null}
      <span>{children}</span>
    </button>
  );
});

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: 'neutral' | 'purple' | 'cyan' | 'gold' | 'danger' | 'success';
}

export function Badge({ tone = 'neutral', className, ...props }: BadgeProps) {
  return <span className={join('dd-badge', `dd-badge--${tone}`, className)} {...props} />;
}

export function BrandMark({ compact = false }: { compact?: boolean }) {
  return (
    <div className="dd-brand" aria-label="Deffy Direct">
      <span className="dd-brand__sigil" aria-hidden="true">
        Ð
      </span>
      {compact ? null : (
        <span className="dd-brand__name">
          <strong>DEFFY</strong>
          <small>DIRECT</small>
        </span>
      )}
    </div>
  );
}

export function FieldMessage({
  children,
  error = false,
}: {
  children: ReactNode;
  error?: boolean;
}) {
  return (
    <p
      className={join('dd-field-message', error && 'dd-field-message--error')}
      role={error ? 'alert' : undefined}
    >
      {children}
    </p>
  );
}
